import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { X, Search, Link2, Loader2, PlusCircle, ExternalLink } from "lucide-react";

/**
 * Iter222 · Modal para vincular manualmente una oferta TIC con una Licitación
 * (colección `ofertas_comercial`).
 *
 * Casos de uso:
 *   1. El matcher automático no encuentra correspondencia → el usuario busca
 *      manualmente y selecciona una licitación.
 *   2. La oferta TIC está presentada (approved/pending_cfo_approval) pero no
 *      existe la licitación en el CRM → botón "Crear como PRESENTADA".
 */
export default function LinkLicitacionModal({ open, offer, onClose, onLinked }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState(false);
  const [creatingNew, setCreatingNew] = useState(false);

  const load = useCallback(async (query) => {
    if (!offer?.id) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/offers/${offer.id}/link-candidates`, {
        params: { q: query || "", limit: 25 },
      });
      setItems(data.items || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [offer?.id]);

  useEffect(() => {
    if (open && offer?.id) {
      setQ("");
    }
  }, [open, offer?.id]);

  // Iter249 · Antes solo buscaba con Enter; el usuario escribía y no pasaba
  // nada. Ahora dispara la búsqueda con debounce al teclear (y al abrir).
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => load(q), 300);
    return () => clearTimeout(t);
  }, [q, open, load]);

  const linkTo = async (licitacion_id) => {
    if (linking) return;
    setLinking(true);
    try {
      const { data } = await axios.post(`${API}/offers/${offer.id}/link-licitacion`, { licitacion_id, create_missing: false });
      onLinked?.(data);
      onClose?.();
    } catch (e) {
      alert(e?.response?.data?.detail || "No se pudo vincular");
    } finally { setLinking(false); }
  };

  const createAndLink = async () => {
    if (creatingNew) return;
    if (!window.confirm(`Se creará una nueva Licitación en el CRM con estado PRESENTADA, arrastrando datos de la oferta "${offer?.title}". ¿Continuar?`)) return;
    setCreatingNew(true);
    try {
      const { data } = await axios.post(`${API}/offers/${offer.id}/link-licitacion`, { create_missing: true });
      onLinked?.(data);
      onClose?.();
    } catch (e) {
      alert(e?.response?.data?.detail || "No se pudo crear/vincular");
    } finally { setCreatingNew(false); }
  };

  const unlink = async () => {
    if (!window.confirm("¿Desvincular esta oferta de su Licitación?")) return;
    try {
      await axios.post(`${API}/offers/${offer.id}/unlink-licitacion`);
      onLinked?.({ ok: true, linked_licitacion_id: null });
      onClose?.();
    } catch (e) {
      alert(e?.response?.data?.detail || "No se pudo desvincular");
    }
  };

  if (!open) return null;
  const isLinked = !!offer?.linked_licitacion_id;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="link-licit-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h2 className="font-bold text-gray-900 flex items-center gap-2 text-lg font-['Outfit']">
              <Link2 size={18} className="text-blue-600" /> Vincular con Licitación
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {isLinked ? (
                <>Actualmente vinculada · <b className="text-blue-700">{offer?.linked_licitacion_state || "—"}</b></>
              ) : (
                <>Oferta TIC sin licitación asociada</>
              )}
            </p>
          </div>
          <button onClick={onClose} data-testid="link-licit-close-btn"><X size={18} className="text-gray-400" /></button>
        </div>

        <div className="p-5 space-y-3 overflow-y-auto flex-1">
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
            <p className="text-xs text-blue-800">
              <b>Oferta:</b> {offer?.title} {offer?.municipality ? `· ${offer.municipality}` : ""}
              {offer?.licitacion ? <> · Exp. <span className="font-mono">{offer.licitacion}</span></> : null}
            </p>
          </div>

          {/* Auto-create as PRESENTADA */}
          {["approved", "pending_cfo_approval", "pending_it_validation", "pending_validation"].includes(offer?.status) && !isLinked && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <p className="text-sm font-semibold text-emerald-900 mb-1 flex items-center gap-2">
                <PlusCircle size={14} /> Crear contrapartida automáticamente
              </p>
              <p className="text-[11px] text-emerald-800 mb-2">
                Esta oferta TIC está presentada. Si no existe la licitación en el CRM, puedes crearla con estado <b>PRESENTADA</b> arrastrando datos.
              </p>
              <button
                onClick={createAndLink}
                disabled={creatingNew}
                className="bg-emerald-600 text-white text-xs font-semibold px-3 py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-60 flex items-center gap-1.5"
                data-testid="link-licit-create-btn"
              >
                {creatingNew ? <Loader2 size={12} className="animate-spin" /> : <PlusCircle size={12} />}
                Crear en Licitaciones como PRESENTADA
              </button>
            </div>
          )}

          {/* Search box */}
          <div className="relative">
            <Search size={14} className="absolute left-3 top-3 text-gray-400" />
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              onKeyDown={e => e.key === "Enter" && load(q)}
              placeholder="Buscar por objeto del contrato / expediente / cliente / provincia…"
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#007934]"
              data-testid="link-licit-search-input"
            />
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8"><Loader2 size={22} className="animate-spin text-gray-400" /></div>
          ) : items.length === 0 ? (
            <p className="text-center text-gray-400 text-sm py-8">No se encontraron licitaciones. Prueba a buscar por otras palabras clave.</p>
          ) : (
            <div className="space-y-2">
              {items.map(lic => {
                const isCurrent = lic.id === offer?.linked_licitacion_id;
                return (
                  <div key={lic.id} className={`border rounded-lg p-3 flex items-start justify-between gap-3 ${isCurrent ? "border-blue-300 bg-blue-50/50" : "border-gray-100 hover:bg-gray-50"}`}
                       data-testid={`link-licit-candidate-${lic.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-gray-900 truncate">{lic.objeto_contrato || "—"}</p>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-[11px] text-gray-500">
                        {lic.expediente && <span>Exp. <span className="font-mono text-gray-700">{lic.expediente}</span></span>}
                        {lic.cliente && <span>{lic.cliente}</span>}
                        {lic.provincia && <span>{lic.provincia}</span>}
                        {lic.estado_licitacion && (
                          <span className="inline-block bg-slate-100 text-slate-700 px-2 py-0.5 rounded font-semibold uppercase text-[9px]">
                            {lic.estado_licitacion}{lic.fase_adjudicacion ? ` · ${lic.fase_adjudicacion}` : ""}
                          </span>
                        )}
                      </div>
                    </div>
                    {isCurrent ? (
                      <span className="text-[10px] font-semibold text-blue-700 uppercase self-center">Actual</span>
                    ) : (
                      <button
                        onClick={() => linkTo(lic.id)}
                        disabled={linking}
                        className="bg-[#007934] text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-[#006528] disabled:opacity-60 flex items-center gap-1"
                        data-testid={`link-licit-select-${lic.id}`}
                      >
                        <Link2 size={11} /> Vincular
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
          {isLinked ? (
            <button onClick={unlink} className="text-xs text-rose-600 hover:text-rose-700 font-semibold flex items-center gap-1" data-testid="link-licit-unlink-btn">
              <ExternalLink size={12} /> Desvincular
            </button>
          ) : <span />}
          <button onClick={onClose} className="border border-gray-200 text-gray-700 rounded-lg px-3 py-1.5 text-sm hover:bg-gray-100">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
